import { Box, Stack } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

interface ScheduleSliderDotsBalloonProps {
  /** Quantidade total de cards no slider */
  total: number;
  /** Índice do card ativo (0-based) */
  activeIndex: number;
  /** Ao tocar em uma bolinha, navega para o card correspondente */
  onDotClick?: (index: number) => void;
  compact?: boolean;
}

/**
 * Balão de paginação do slider mobile — uma bolinha por consulta,
 * preenchida apenas na posição do card visível.
 */
export function ScheduleSliderDotsBalloon({
  total,
  activeIndex,
  onDotClick,
  compact = false,
}: ScheduleSliderDotsBalloonProps) {
  const theme = useTheme();

  if (total <= 1) return null;

  return (
    <Stack alignItems="center" sx={{ width: "100%" }}>
      <Box
        role="tablist"
        aria-label="Posição das consultas agendadas"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: compact ? 0.55 : 0.65,
          px: compact ? 0.85 : 1.25,
          py: compact ? 0.35 : 0.55,
          borderRadius: 99,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          border: "1px solid",
          borderColor: alpha(theme.palette.primary.main, 0.18),
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}`,
        }}
      >
        {Array.from({ length: total }, (_, index) => {
          const isActive = index === activeIndex;

          return (
            <Box
              key={index}
              role="tab"
              aria-selected={isActive}
              aria-label={`Consulta ${index + 1} de ${total}`}
              onClick={() => onDotClick?.(index)}
              sx={{
                width: isActive ? (compact ? 8 : 9) : compact ? 6 : 7,
                height: isActive ? (compact ? 8 : 9) : compact ? 6 : 7,
                borderRadius: "50%",
                flexShrink: 0,
                cursor: onDotClick ? "pointer" : "default",
                bgcolor: isActive
                  ? "primary.main"
                  : alpha(theme.palette.primary.main, 0.22),
                transition:
                  "width 0.2s ease, height 0.2s ease, background-color 0.2s ease",
                "&:hover": onDotClick
                  ? {
                      bgcolor: isActive
                        ? "primary700.main"
                        : alpha(theme.palette.primary.main, 0.35),
                    }
                  : undefined,
              }}
            />
          );
        })}
      </Box>
    </Stack>
  );
}
